import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

/**
 * Auto-shows a TOTP unlock dialog when an admin with 2FA enabled has not
 * unlocked their session yet. Also listens for a global "admin-unlock-required"
 * event fired by our axios interceptor (see api.js) when the server responds
 * 403 with detail "2FA_REQUIRED".
 */
export default function AdminUnlockDialog() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user && user.role === "admin";
  const status = useQuery({
    queryKey: ["totp-status"],
    queryFn: async () => (await api.get("/auth/2fa/status")).data,
    enabled: !!isAdmin,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (isAdmin && status.data && status.data.enabled && !status.data.admin_unlocked) {
      setOpen(true);
    }
  }, [isAdmin, status.data]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("admin-unlock-required", handler);
    return () => window.removeEventListener("admin-unlock-required", handler);
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/unlock", { code });
      toast.success("Console admin déverrouillée (4h)");
      setOpen(false);
      setCode("");
      qc.invalidateQueries({ queryKey: ["totp-status"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
      <DialogContent className="bg-card border-white/10 max-w-sm" data-testid="admin-unlock-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <DialogTitle>Déverrouiller la console admin</DialogTitle>
          </div>
          <p className="text-xs text-zinc-400">
            Entrez le code TOTP à 6 chiffres de votre app authenticator. Déverrouillage valable 4h.
          </p>
        </DialogHeader>
        <div>
          <Label>Code</Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            maxLength={6}
            autoFocus
            className="mt-2 font-mono-num text-lg tracking-[0.4em] text-center bg-white/[0.03] border-white/10"
            data-testid="admin-unlock-code"
            onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) submit(); }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Plus tard</Button>
          <Button onClick={submit} disabled={code.length !== 6 || submitting} className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="admin-unlock-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Déverrouiller"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
